import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay";
import { PAGE_STATE } from "../../../constants";
import useTickets from "../../../hooks/useTickets";
import useUsers from "../../../hooks/useUsers";
import { addSeconds, format } from "date-fns";
import Button from "../../common/Button";
import AppContext from "../../../AppContext";
import { Formik, Form } from "formik";

import { ReactComponent as SendSvg } from "../../../assets/icons/send.svg";
import { ReactComponent as AttachmentSvg } from "../../../assets/icons/chat/attachment.svg";
import { ReactComponent as TrashSvg } from "../../../assets/icons/trash.svg";

import AttachmentView from "./AttachmentView";

const SupportTag = () => (
  <div className="admin-ticket__support-tag">Urbaan support</div>
);

const View = ({ ticketId, onClose }) => {
  const {
    getTicket,
    queryTicketComments,
    bookTicket,
    addComment,
    closeTicket,
    uploadTicketCommentAttachments,
    patchTicketComment,
  } = useTickets();
  const { getUser, getStaff } = useUsers();
  const { user: staff } = useContext(AppContext);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [ticket, setTicket] = useState(undefined);
  const inputRef = useRef();
  const [attachments, setAttachments] = useState([]);
  const [isToggleViewAttachment, setIsToggleViewAttachment] = useState(false);
  const [selectedImage, setSelectedImage] = useState(undefined);

  useEffect(() => {
    fetchData();
    return () => {};
  }, []);

  const fetchData = async () => {
    // * Get ticket
    let ticket = await getTicket(ticketId);
    ticket.booked = false;

    // * Get user
    const user = await getUser(ticket.createdBy);
    ticket.createdBy = user;

    // * Query comments and get staff info if ticket has staff assigned
    if (ticket.staff) {
      const [commentResult, staffResult] = await Promise.allSettled([
        queryTicketComments(
          ticket._id,
          { active: true, deleted: false },
          null,
          -1
        ),
        getStaff(ticket.staff),
      ]);

      if (commentResult.status === "fulfilled") {
        ticket.comments = commentResult.value.data;
      }

      if (staffResult.status === "fulfilled") {
        ticket.support = staffResult.value;
      }

      ticket.booked = true;
    }

    setTicket(ticket);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleStartSupportClick = async () => {
    await bookTicket(ticket._id);

    setTicket({
      ...ticket,
      booked: true,
      support: staff,
    });
  };

  const handleCloseTicketClick = async () => {
    const { closedOn } = await closeTicket(ticket._id);

    setTicket({
      ...ticket,
      closed: true,
      closedOn,
    });
  };

  const handleCommentSubmit = async ({ msg }, { resetForm }) => {
    const _newComment = await addComment(
      ticket._id,
      msg,
      attachments.length === 0
    );

    if (!_newComment.active && attachments.length > 0) {
      _newComment.attachments = await uploadTicketCommentAttachments(
        ticket._id,
        _newComment._id,
        attachments
      );

      // * Patch ticket back to active
      await patchTicketComment(ticket._id, _newComment._id, { active: true });
    }

    // * Add comment to state
    setTicket((curr) => ({
      ...curr,
      comments: [...curr.comments, _newComment],
    }));
    setAttachments([]);
    resetForm();
  };

  if (pageState !== PAGE_STATE.IDLE || !ticket) {
    return (
      <Overlay
        onClose={onClose}
        content={<div className="admin-ticket">Loading</div>}
      />
    );
  }

  const handleAttachmentChanged = (event) => {
    if (!event.target.files) return;
    setAttachments([...event.target.files]);
  };

  const handleRemoveImage = (file) => {
    setAttachments((curr) => [...curr.filter((f) => f.name !== file.name)]);
  };

  const handleAttachmentClick = (src) => {
    setSelectedImage(src);
    setIsToggleViewAttachment(true);
  };

  const handleAttachmentViewClose = () => {
    setSelectedImage(undefined);
    setIsToggleViewAttachment(false);
  };

  const {
    _id,
    no,
    topic,
    desc,
    comments,
    createdBy,
    createdOn,
    booked,
    support,
    closed,
    closedOn,
  } = ticket;

  return (
    <Overlay
      style={{
        alignItems: "flex-start",
      }}
      onClose={onClose}
      content={
        <div className="admin-ticket">
          <div className="admin-ticket-header">
            <img
              className="admin-ticket-header__avatar"
              src={createdBy.avatar}
            />
            <div className="admin-ticket-header-title">
              <div className="admin-ticket-header-title__id">
                #{(no ?? 1).toString().padStart(6, "0")}
              </div>
              {booked && !closed && (
                <Button
                  label={"Close ticket"}
                  onClick={handleCloseTicketClick}
                />
              )}
            </div>
            <div className="admin-ticket-header__topic">{topic}</div>
            <div className="admin-ticket-header-info">
              <span className="admin-ticket-header-info__label">Opened by</span>
              <span className="admin-ticket-header-info__label admin-ticket-header-info__label--link">
                {createdBy.firstname} {createdBy.surname || ""}
              </span>
              <span className="admin-ticket-header-info__label">
                on {format(new Date(createdOn), "MMM dd, yyyy")}
                {support ? ", " : ""}
              </span>
              {support && (
                <>
                  <span className="admin-ticket-header-info__label">
                    Answered by
                  </span>
                  <span className="admin-ticket-header-info__label admin-ticket-header-info__label--link">
                    {support.firstname} {support.lastname || ""}
                  </span>
                  <SupportTag />
                </>
              )}
            </div>
          </div>
          <div className="admin-ticket-desc">{desc}</div>
          {booked && comments && (
            <div className="admin-ticket-comments">
              <div className="admin-ticket-comments__count">
                {comments.length} Comments
              </div>
              {comments.map((c) => {
                const _isSupport = c.createdBy === support._id;

                return (
                  <div className="admin-ticket-comments-comment" key={c._id}>
                    <img
                      className="admin-ticket-comments-comment__owner"
                      src={_isSupport ? support.avatar : createdBy.avatar}
                    />
                    <div className="admin-ticket-comments-comment__arrow"></div>
                    <div className="admin-ticket-comments-comment-info">
                      <div className="admin-ticket-comments-comment-info-header">
                        <span className="admin-ticket-comments-comment-info-header__name">
                          {_isSupport
                            ? `${support.firstname} ${support.lastname || ""}`
                            : `${createdBy.firstname} ${
                                createdBy.surname || ""
                              }`}
                        </span>
                        <span className="admin-ticket-comments-comment-info-header__date">
                          on {format(new Date(c.createdOn), "MMM dd, yyyy")}
                        </span>
                        {_isSupport && <SupportTag />}
                      </div>
                      <p className="admin-ticket-comments-comment-info-value">
                        {c.comment}
                      </p>
                      {c.attachments && (
                        <div className="admin-ticket-comments-comment-info-attachments">
                          {c.attachments.map(({ name, file, mimeType }) => {
                            return (
                              <div className="admin-ticket-comments-comment-info-attachments-item">
                                <img
                                  className="admin-ticket-comments-comment-info-attachments-item__image"
                                  alt="attachment"
                                  src={file}
                                  onClick={() => {
                                    // TODO: Check mimeType
                                    handleAttachmentClick(file);
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {!closed && (
                <div
                  className="chat-input"
                  style={{
                    padding: "0",
                    marginTop: 24,
                  }}
                >
                  <div className="chat-input-actions">
                    <input
                      id={`attachment`}
                      className="fileuploader-original"
                      type="file"
                      accept="image/*"
                      onChange={handleAttachmentChanged}
                      multiple
                    />
                    <label
                      className="chat-input-actions-action"
                      htmlFor={`attachment`}
                    >
                      <AttachmentSvg />
                    </label>
                  </div>
                  <Formik
                    initialValues={{ msg: "" }}
                    onSubmit={handleCommentSubmit}
                  >
                    {({ values, setFieldValue, submitForm }) => {
                      const handleInputChanged = ({ target: { value } }) => {
                        setFieldValue("msg", value);
                      };

                      return (
                        <Form className="chat-input-textbox">
                          <input
                            ref={inputRef}
                            className="chat-input-textbox__input"
                            placeholder="Type a message"
                            value={values.msg}
                            onChange={handleInputChanged}
                          />
                          <SendSvg
                            className="chat-input-textbox__send-button"
                            onClick={submitForm}
                          />
                        </Form>
                      );
                    }}
                  </Formik>
                  <div
                    className={`chat-input-attachments ${
                      attachments.length > 0
                        ? "chat-input-attachments--exists"
                        : ""
                    }`}
                  >
                    {attachments.map((attachment) => {
                      return (
                        <div className="chat-input-attachments-item">
                          <img
                            className="chat-input-attachments-item__image"
                            alt="attachment"
                            src={URL.createObjectURL(attachment)}
                            onClick={() => {
                              // TODO: Check mimeType
                              handleAttachmentClick(
                                URL.createObjectURL(attachment)
                              );
                            }}
                          />
                          <TrashSvg
                            className="chat-input-attachments-item__trash"
                            onClick={() => handleRemoveImage(attachment)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {!booked && !closed && (
            <div className="admin-ticket-book">
              <Button
                label={"Start support"}
                onClick={handleStartSupportClick}
              />
            </div>
          )}
          {closed && (
            <span className="admin-ticket__closed-info">
              This ticket has been closed on{" "}
              {format(new Date(closedOn), "MMM dd, yyyy HH:mm")}
            </span>
          )}
          {isToggleViewAttachment && selectedImage && (
            <AttachmentView
              onClose={handleAttachmentViewClose}
              src={selectedImage}
            />
          )}
        </div>
      }
    />
  );
};

View.propTypes = {
  ticketId: PropTypes.string.isRequired,
};

export default View;
